import * as React from "react";
import { PropsWithChildren } from "react";
import { useApiService } from "../hooks";
import { IngenicoSettings, LanguageApi, LanguageDto, SystemApi, SystemVersionDto } from "../api";
import i18n from "i18next";
import { useEventsContext } from "./EventContext";
import { useLocalStorageState, useRequest } from "ahooks";

interface IGlobalDataContext {
  systemInfo?: SystemVersionDto;
  ingenicoSettings?: IngenicoSettings;
  currentLanguage?: LanguageDto;
  setLanguage?: (language: LanguageDto) => void;
  setLanguageCode?: (language: string) => void;
  appVersion: string;
  shortVersion: string;
  backendNotReacheable: boolean;
  loading: boolean;
  languages: LanguageDto[];
  retry: () => void;
}

const GlobalDataContext = React.createContext<IGlobalDataContext>(null);

interface IGlobalDataProviderProps extends PropsWithChildren {}

export const GlobalDataProvider: React.FunctionComponent<IGlobalDataProviderProps> = ({ children }) => {
  const systemApi = useApiService(SystemApi);
  const languageApi = useApiService(LanguageApi);
  const [currentLanguage, setCurrentLanguage] = useLocalStorageState<LanguageDto>("currentLanguage");

  const { dispatchEvent } = useEventsContext();

  const setLanguage = React.useCallback(
    (lang: LanguageDto) => {
      setCurrentLanguage(lang);
      dispatchEvent("LanguageChanged", currentLanguage);
    },
    [currentLanguage, dispatchEvent, setCurrentLanguage]
  );
  const {
    data: globalData,
    loading: globalDataLoading,
    error,
    refresh: trigger,
  } = useRequest(async () => {
    const [systemInfo, languages, ingenicoSettings] = await Promise.all([
      systemApi.get(),
      languageApi.getLanguages(true),
      systemApi.ingenicoSettings(),
    ]);

    return { systemInfo, languages, ingenicoSettings };
  });

  const findLanguage = React.useCallback(
    (lang: string) => {
      const langFound = globalData?.languages.find((d) => d.languageCode === lang);
      if (langFound) {
        return langFound;
      } else {
        return LanguageDto.fromJS({ languageId: 2, languageCode: "NL" });
      }
    },
    [globalData?.languages]
  );

  const setLanguageCode = React.useCallback(
    (lang: string) => {
      const lg = findLanguage(lang);
      setLanguage(lg);
    },
    [findLanguage, setLanguage]
  );

  React.useEffect(() => {
    i18n.changeLanguage(currentLanguage?.languageCode || "FR");
  }, [currentLanguage]);

  React.useEffect(() => {
    if (currentLanguage === null || currentLanguage?.languageCode === "EN") {
      setCurrentLanguage(findLanguage("FR"));
    }
  }, [currentLanguage, findLanguage, setCurrentLanguage]);

  const appVersion = React.useMemo(() => {
    if (globalData?.systemInfo?.applicationVersion) {
      console.log("SRSH - Care4Dogs");
      console.log("Version " + globalData?.systemInfo?.applicationVersion);
      return globalData?.systemInfo?.applicationVersion.split(".Branch")[0];
    } else {
      return "";
    }
  }, [globalData]);

  const backendNotReachable = React.useMemo(() => globalDataLoading || !!error, [error, globalDataLoading]);

  return (
    <GlobalDataContext.Provider
      value={{
        systemInfo: globalData?.systemInfo,
        ingenicoSettings: globalData?.ingenicoSettings,
        loading: globalDataLoading,
        backendNotReacheable: backendNotReachable,
        appVersion,
        retry: trigger,
        shortVersion: appVersion?.split("+")[0],
        setLanguage,
        setLanguageCode,
        currentLanguage: currentLanguage,
        languages: globalData?.languages,
      }}
    >
      {children}
    </GlobalDataContext.Provider>
  );
};

export const useGlobalData = () => React.useContext(GlobalDataContext);
